import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// ─── Supported document types ─────────────────────────────────────────────────

/**
 * Logical document categories — used as the first segment of the S3 key.
 * Add new types here as the platform grows.
 */
export const DOCUMENT_TYPES = [
  'consent-forms',
  'consent-sign',
  'supporting-docs',
  'screening-reports',
  'discharge-summaries',
  'lab-results',
  'other',
] as const;

export const DocumentTypeSchema = z.enum(DOCUMENT_TYPES);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

// ─── Allowed MIME types ────────────────────────────────────────────────────────

export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
] as const;

export const MimeTypeSchema = z.enum(ALLOWED_MIME_TYPES);
export type MimeType = z.infer<typeof MimeTypeSchema>;

// ─── Route params ─────────────────────────────────────────────────────────────

/**
 * Params for both upload and get:
 *   POST /documents/:hospital_id/:subject_type/:subject_id
 *   GET  /documents/:hospital_id/:subject_type/:subject_id/:doc_id
 */
export const DocumentUploadParamsSchema = z.object({
  hospital_id:  uuidSchema,
  subject_type: z.string().min(1).max(50).describe('e.g. milk-provider, patient'),
  subject_id:   uuidSchema,
});
export type DocumentUploadParams = z.infer<typeof DocumentUploadParamsSchema>;

export const DocumentGetParamsSchema = z.object({
  hospital_id: uuidSchema,
});
export type DocumentGetParams = z.infer<typeof DocumentGetParamsSchema>;

// ─── Query params ─────────────────────────────────────────────────────────────

export const DocumentUploadQuerySchema = z.object({
  doc_type:        DocumentTypeSchema.describe('Logical document category'),
  staff_id:        uuidSchema.optional().describe(
    'UUID of the staff member performing the upload. ' +
    'When provided, this overrides the authenticated user\'s JWT sub as the uploaded_by value.',
  ),
  consent_id:      uuidSchema.optional().describe(
    'Required when doc_type is "consent-forms" or "consent-sign". ' +
    'The uploaded URL will be written to consent_document_ref or digital_signature_ref on the matching clinical_consent_records row.',
  ),
  health_record_id: uuidSchema.optional().describe(
    'Required when doc_type is "screening-reports". ' +
    'Must belong to the subject (subject_id in the path). ' +
    'A new row is created in milk_provider_health_record_attachments with the uploaded URL in file_path.',
  ),
});
export type DocumentUploadQuery = z.infer<typeof DocumentUploadQuerySchema>;

/**
 * Query params for bulk upload.
 * doc_type is per-file (inside the metadata body field), not a top-level param.
 */
export const DocumentBulkUploadQuerySchema = z.object({
  staff_id: uuidSchema.optional().describe(
    'UUID of the staff member performing the upload. ' +
    'When provided, this overrides the authenticated user\'s JWT sub as the uploaded_by value.',
  ),
  hospital_id: uuidSchema.optional().describe('Hospital context UUID'),
  subject_type: z.string().max(50).optional().describe('e.g. DONOR, MOTHER, PATIENT'),
  subject_id: uuidSchema.optional().describe('Subject (person) UUID'),
  health_record_id: uuidSchema.optional().describe('Health record UUID for screening-reports doc_type'),
});
export type DocumentBulkUploadQuery = z.infer<typeof DocumentBulkUploadQuerySchema>;

export const DocumentGetQuerySchema = z.object({
  url: z.string().url().describe(
    'Permanent document URL returned at upload time. ' +
    'The S3 key is extracted from this URL to locate the object in the bucket.',
  ),
});
export type DocumentGetQuery = z.infer<typeof DocumentGetQuerySchema>;

// ─── Response schemas ─────────────────────────────────────────────────────────

/**
 * Response for POST /documents/…  (upload)
 * Returns the stored S3 key and the permanent document URL.
 */
export const DocumentUploadResponseSchema = z.object({
  doc_id:      uuidSchema.describe('Unique identifier for this document — store alongside your record'),
  s3_key:      z.string().describe('Full S3 object key'),
  doc_type:    DocumentTypeSchema,
  content_type: z.string().describe('MIME type of the uploaded file'),
  size_bytes:  z.number().int().nonnegative(),
  url:         z.string().url().describe('Permanent document URL — safe to persist in the database'),
  uploaded_at: z.string().datetime().describe('ISO 8601 upload timestamp'),
});
export type DocumentUploadResponse = z.infer<typeof DocumentUploadResponseSchema>;

/**
 * Response for GET /documents/fetch  (retrieve document by URL)
 * Returns the S3 key and the same permanent URL passed in, confirming the object exists.
 */
export const DocumentGetResponseSchema = z.object({
  s3_key: z.string().describe('Full S3 object key resolved from the URL'),
  url:    z.string().url().describe('Permanent document URL — same as supplied in the request'),
});
export type DocumentGetResponse = z.infer<typeof DocumentGetResponseSchema>;

// ─── Per-file metadata (bulk upload) ──────────────────────────────────────────

/**
 * Metadata sent alongside each file in a bulk upload request.
 * The client sends a JSON array in a `metadata` multipart text field.
 * Each entry is matched by index to the corresponding file field.
 */
export const DocumentFileMetadataSchema = z.object({
  doc_type:         DocumentTypeSchema.describe('Document category for this file'),
  patient_id:       uuidSchema.optional().describe('Optional patient UUID this document belongs to'),
  person_id:        uuidSchema.optional().describe('Optional person (milk-provider) UUID'),
  encounter_id:     uuidSchema.optional().describe('Optional encounter UUID'),
  consent_id:       uuidSchema.optional().describe('Optional consent record UUID'),
  health_record_id: uuidSchema.optional().describe('Optional health-record UUID'),
  notes:            z.string().max(500).optional().describe('Optional free-text note for this file'),
});
export type DocumentFileMetadata = z.infer<typeof DocumentFileMetadataSchema>;

/**
 * Request-level schema for the metadata text field.
 * Validated after JSON.parse() in the controller.
 */
export const DocumentBulkMetadataSchema = z
  .array(DocumentFileMetadataSchema)
  .min(1)
  .max(10)
  .describe('JSON array — one entry per file, matched by index');
export type DocumentBulkMetadata = z.infer<typeof DocumentBulkMetadataSchema>;

// ─── Bulk upload ──────────────────────────────────────────────────────────────

/**
 * One entry in the bulk upload response array.
 * `status` is 'ok' on success or 'error' on per-file failure.
 */
export const DocumentBulkUploadItemSchema = z.discriminatedUnion('status', [
  z.object({
    status:       z.literal('ok'),
    filename:     z.string(),
    doc_id:       z.string().uuid(),
    s3_key:       z.string(),
    doc_type:     DocumentTypeSchema,
    content_type: z.string(),
    size_bytes:   z.number().int().nonnegative(),
    url:          z.string().url().describe('Permanent document URL — safe to persist in the database'),
    uploaded_at:  z.string().datetime(),
    patient_id:       z.string().uuid().optional(),
    person_id:        z.string().uuid().optional(),
    encounter_id:     z.string().uuid().optional(),
    consent_id:       z.string().uuid().optional(),
    health_record_id: z.string().uuid().optional(),
    notes:            z.string().optional(),
  }),
  z.object({
    status:   z.literal('error'),
    filename: z.string(),
    reason:   z.string(),
  }),
]);
export type DocumentBulkUploadItem = z.infer<typeof DocumentBulkUploadItemSchema>;

/**
 * Response for POST /documents/…/bulk
 * Returns one entry per uploaded file (in the same order as the request).
 */
export const DocumentBulkUploadResponseSchema = z.object({
  total:     z.number().int().nonnegative().describe('Total files received'),
  succeeded: z.number().int().nonnegative().describe('Files successfully uploaded'),
  failed:    z.number().int().nonnegative().describe('Files that failed'),
  results:   z.array(DocumentBulkUploadItemSchema),
});
export type DocumentBulkUploadResponse = z.infer<typeof DocumentBulkUploadResponseSchema>;

// ─── PATCH document schemas ───────────────────────────────────────────────────

export const DocumentPatchDocTypeSchema = z.enum(['clinical', 'health_record']);
export type DocumentPatchDocType = z.infer<typeof DocumentPatchDocTypeSchema>;

/**
 * PATCH /documents/update-document
 *
 * When doc_type = 'clinical':
 *   Updates digital_signature_ref or consent_document_ref on a clinical_consent_records row.
 *   After update, sets withdrawn_at = today on the previously active consent document.
 *   Requires: subject_id (person_id) + document_id (consent_id).
 *
 * When doc_type = 'health_record':
 *   Updates a specific attachment on a health record.
 *   A single test can have multiple documents — specify health_record_id + attachment_id.
 *   Activates the specified attachment (is_active = true).
 *   Optionally deactivates other attachment_ids via remove_attachment_ids.
 */
export const PatchDocumentRequestSchema = z.discriminatedUnion('doc_type', [
  // ── Clinical consent document update ────────────────────────────────────────
  z.object({
    doc_type:              z.literal('clinical'),
    subject_id:            uuidSchema.describe('UUID of the milk provider (person_id) — subject of the consent record'),
    document_id:           uuidSchema.describe('UUID of the clinical_consent_records row (consent_id) to update'),
  }),
  // ── Health record attachment update ─────────────────────────────────────────
  z.object({
    doc_type:              z.literal('health_record'),
    health_record_id:      uuidSchema.describe('UUID of the milk_provider_health_records row'),
    attachment_id:         uuidSchema.describe('UUID of the attachment to activate (set is_active = true)'),
    remove_attachment_ids: z.array(uuidSchema).optional().describe('Attachment IDs to deactivate (set is_active = false) — use when removing specific documents from a set'),
    hospital_id:           uuidSchema,
  }),
  // ── Supporting document removal ─────────────────────────────────────────────
  z.object({
    doc_type:              z.literal('supporting_document'),
    attachment_id:         uuidSchema.describe('UUID of the person_document_attachments row to deactivate (set is_active = false)'),
    hospital_id:           uuidSchema.describe('Hospital UUID — verified against the attachment\'s hospital_id before deactivation'),
  }),
]);
export type PatchDocumentRequest = z.infer<typeof PatchDocumentRequestSchema>;

export const PatchDocumentClinicalResponseSchema = z.object({
  consent_id:            uuidSchema,
  previous_consent_id:   uuidSchema.nullable().describe('Consent ID of the previously active record that was withdrawn (null if none)'),
  withdrawn_at:          z.string().datetime({ offset: true }).nullable(),
});
export type PatchDocumentClinicalResponse = z.infer<typeof PatchDocumentClinicalResponseSchema>;

export const PatchDocumentHealthRecordResponseSchema = z.object({
  health_record_id:      uuidSchema,
  activated_attachment:   uuidSchema,
  deactivated_attachments: z.array(uuidSchema),
});
export type PatchDocumentHealthRecordResponse = z.infer<typeof PatchDocumentHealthRecordResponseSchema>;

export const PatchDocumentSupportingDocumentResponseSchema = z.object({
  attachment_id: uuidSchema,
  is_active:     z.literal(false),
});
export type PatchDocumentSupportingDocumentResponse = z.infer<typeof PatchDocumentSupportingDocumentResponseSchema>;

export const PatchDocumentResponseSchema = z.union([
  PatchDocumentClinicalResponseSchema,
  PatchDocumentHealthRecordResponseSchema,
  PatchDocumentSupportingDocumentResponseSchema,
]);
export type PatchDocumentResponse = z.infer<typeof PatchDocumentResponseSchema>;

// ─── GET documents by person ───────────────────────────────────────────────────

export const GetDocumentsByPersonParamsSchema = z.object({
  person_id: uuidSchema,
});
export type GetDocumentsByPersonParams = z.infer<typeof GetDocumentsByPersonParamsSchema>;

export const AttachmentDocumentItemSchema = z.object({
  attachment_id:  uuidSchema,
  document_type:  z.string(),
  file_name:      z.string(),
  file_url:       z.string(),
  uploaded_at:    z.string().datetime({ offset: true }),
});
export type AttachmentDocumentItem = z.infer<typeof AttachmentDocumentItemSchema>;

/**
 * A "Supporting Document" uploaded from the Consent & Documents step
 * (doc_type = 'supporting-docs'), stored in person_document_attachments.
 * Keyed by subject_type/subject_id — NOT by consent_id — so it works for
 * MOTHER, DONOR and BOTH roles alike (a MOTHER-only person never has a
 * clinical_consent_records row).
 */
export const SupportingDocumentItemSchema = z.object({
  attachment_id:   uuidSchema,
  file_name:       z.string(),
  file_url:        z.string(),
  file_size_bytes: z.number().int().nonnegative().nullable(),
  mime_type:       z.string().nullable(),
  uploaded_at:     z.string().datetime({ offset: true }),
  is_active:       z.boolean(),
});
export type SupportingDocumentItem = z.infer<typeof SupportingDocumentItemSchema>;

export const ConsentDocumentItemSchema = z.object({
  consent_id:            uuidSchema,
  hospital_id:           uuidSchema,
  purpose_id:            uuidSchema,
  is_consent_given:      z.boolean(),
  consent_method:        z.string(),
  consent_document_ref:  z.string().nullable(),
  digital_signature_ref: z.string().nullable(),
  granted_at:            z.string().datetime({ offset: true }).nullable(),
  withdrawn_at:          z.string().datetime({ offset: true }).nullable(),
  created_at:            z.string().datetime({ offset: true }),
  attachments:           z.array(AttachmentDocumentItemSchema),
});
export type ConsentDocumentItem = z.infer<typeof ConsentDocumentItemSchema>;

export const GetDocumentsByPersonResponseSchema = z.object({
  person_id:            uuidSchema,
  consent_document:     ConsentDocumentItemSchema.nullable(),
  supporting_documents: z.array(SupportingDocumentItemSchema),
});
export type GetDocumentsByPersonResponse = z.infer<typeof GetDocumentsByPersonResponseSchema>;
